import React, { useEffect, useRef } from 'react';
import { SharedEffects } from './SharedEffects';

export function SineWaveOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const waves = [
      { amplitude: 80, frequency: 0.0005, speed: 0.003, yOffset: 0.22, opacity: 0.12, width: 1.2, phase: 0 },
      { amplitude: 65, frequency: 0.0006, speed: -0.0025, yOffset: 0.28, opacity: 0.09, width: 1, phase: 1.2 },
      { amplitude: 90, frequency: 0.00045, speed: 0.002, yOffset: 0.45, opacity: 0.11, width: 1.4, phase: 0.5 },
      { amplitude: 70, frequency: 0.00055, speed: -0.0035, yOffset: 0.5, opacity: 0.08, width: 0.8, phase: 2.1 },
      { amplitude: 75, frequency: 0.0005, speed: 0.0015, yOffset: 0.72, opacity: 0.1, width: 1.1, phase: 3.5 },
      { amplitude: 55, frequency: 0.00075, speed: -0.0028, yOffset: 0.78, opacity: 0.07, width: 0.7, phase: 0.8 },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.4;

      waves.forEach((wave, wi) => {
        const baseY = canvas.height * wave.yOffset;

        ctx.beginPath();
        ctx.strokeStyle = `rgba(66, 133, 244, ${wave.opacity * 0.25})`;
        ctx.lineWidth = wave.width * 3;
        ctx.shadowColor = 'rgba(66, 133, 244, 0.2)';
        ctx.shadowBlur = 10;
        for (let x = 0; x <= canvas.width; x += 3) {
          const y = baseY + Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude + Math.sin(x * wave.frequency * 1.7 + time * wave.speed * 0.7 + wave.phase * 0.5) * wave.amplitude * 0.3;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        ctx.beginPath();
        ctx.strokeStyle = `rgba(66, 133, 244, ${wave.opacity})`;
        ctx.lineWidth = wave.width;
        ctx.shadowBlur = 0;
        for (let x = 0; x <= canvas.width; x += 3) {
          const y = baseY + Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude + Math.sin(x * wave.frequency * 1.7 + time * wave.speed * 0.7 + wave.phase * 0.5) * wave.amplitude * 0.3;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        if (wi < waves.length - 1) {
          const nextWave = waves[wi + 1];
          const nextBaseY = canvas.height * nextWave.yOffset;
          for (let x = 0; x <= canvas.width; x += 12) {
            const y1 = baseY + Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude + Math.sin(x * wave.frequency * 1.7 + time * wave.speed * 0.7 + wave.phase * 0.5) * wave.amplitude * 0.3;
            const y2 = nextBaseY + Math.sin(x * nextWave.frequency + time * nextWave.speed + nextWave.phase) * nextWave.amplitude + Math.sin(x * nextWave.frequency * 1.7 + time * nextWave.speed * 0.7 + nextWave.phase * 0.5) * nextWave.amplitude * 0.3;
            const dist = Math.abs(y1 - y2);
            if (dist < 12) {
              const intensity = (1 - dist / 12) * 0.25;
              ctx.beginPath();
              ctx.arc(x, (y1 + y2) / 2, 2 + (1 - dist / 12) * 3, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(66, 133, 244, ${intensity})`;
              ctx.shadowColor = 'rgba(66, 133, 244, 0.3)';
              ctx.shadowBlur = 6;
              ctx.fill();
              ctx.shadowBlur = 0;
            }
          }
        }
      });

      [0, 2, 4].forEach((wi, pi) => {
        const wave = waves[wi];
        const baseY = canvas.height * wave.yOffset;
        const pulseX = ((time * 0.4 + pi * 500) % (canvas.width + 200)) - 100;
        const pulseY = baseY + Math.sin(pulseX * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude + Math.sin(pulseX * wave.frequency * 1.7 + time * wave.speed * 0.7 + wave.phase * 0.5) * wave.amplitude * 0.3;

        if (pulseX > 0 && pulseX < canvas.width) {
          ctx.beginPath();
          ctx.arc(pulseX, pulseY, 6, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(66, 133, 244, 0.1)';
          ctx.shadowColor = 'rgba(66, 133, 244, 0.3)';
          ctx.shadowBlur = 12;
          ctx.fill();
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.arc(pulseX, pulseY, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(66, 133, 244, 0.6)';
          ctx.fill();
        }
      });

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="auth-overlay">
      <canvas ref={canvasRef} />
      <SharedEffects />
    </div>
  );
}
